import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    faAppleWhole,
    faArrowLeft,
    faBadgePercent,
    faBooks,
    faCartShopping,
    faCashRegister,
    faChevronLeft,
    faChevronRight,
    faCompass,
    faDoorOpen,
    faEarthAfrica,
    faTractor,
    faFileContract,
    faFileLock,
    faHatChef,
    faLeaf,
    faLocationDot,
    faLock,
    faMap,
    faMobileScreenButton,
    faMoneyBill,
    faPen,
    faRectangleBarcode,
    faRightToBracket,
    faSearch,
    faTicket,
    faUnlock,
    faUser,
    faWheatSlash,
    faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { BrandIcons } from '@whiz-cart/node-shared/models/brand/brand';
import asDict from '@whiz-cart/node-shared/models/helpers/asDict';
import milkBottleSlash from '@whiz-cart/ui-shared/customFontawesome/milkBottleSlash';
import vLabel from '@whiz-cart/ui-shared/customFontawesome/vLabel';
import { vars } from '@whiz-cart/ui-shared/theme/vars';
import type { CSSProperties, ReactNode } from 'react';

type BrandIconProps = { size: number | string; className?: string; style?: CSSProperties };

export default asDict({
    search: fontAwesomeBrandIcon(faSearch),
    pluHierarchy: fontAwesomeBrandIcon(faAppleWhole),
    pluSearch: fontAwesomeBrandIcon(faRectangleBarcode),
    promotion: fontAwesomeBrandIcon(faBadgePercent),
    sosInactive: (props) => <SosIcon {...props} />,
    sosActive: (props) => <SosIcon active {...props} />,
    lockActive: fontAwesomeBrandIcon(faLock),
    lockInactive: fontAwesomeBrandIcon(faUnlock),
    cart: fontAwesomeBrandIcon(faCartShopping),
    cartPlus: fontAwesomeBrandIcon(faCartShopping),
    navigation: fontAwesomeBrandIcon(faMap),
    arrowLeftTutorial: fontAwesomeBrandIcon(faChevronLeft),
    arrowRightTutorial: fontAwesomeBrandIcon(faChevronRight),
    arrowBack: fontAwesomeBrandIcon(faArrowLeft),
    user: fontAwesomeBrandIcon(faUser),
    location: fontAwesomeBrandIcon(faLocationDot),
    edit: fontAwesomeBrandIcon(faPen),
    exit: fontAwesomeBrandIcon(faDoorOpen),
    exitPopUp: fontAwesomeBrandIcon(faDoorOpen),
    startShopping: fontAwesomeBrandIcon(faRightToBracket),
    startPayment: fontAwesomeBrandIcon(faMoneyBill),
    checkout: fontAwesomeBrandIcon(faCashRegister),
    payByApp: fontAwesomeBrandIcon(faMobileScreenButton),
    payAtRegister: fontAwesomeBrandIcon(faCashRegister),
    navigate: fontAwesomeBrandIcon(faCompass),
    recipes: fontAwesomeBrandIcon(faHatChef),
    language: fontAwesomeBrandIcon(faEarthAfrica),
    close: fontAwesomeBrandIcon(faXmark),
    tutorial: fontAwesomeBrandIcon(faBooks),
    dataPrivacy: fontAwesomeBrandIcon(faFileLock),
    termsAndConditions: fontAwesomeBrandIcon(faFileContract),
    coupon: fontAwesomeBrandIcon(faTicket),
    flagOrganic: fontAwesomeBrandIcon(faLeaf),
    flagLactoseFree: fontAwesomeBrandIcon(milkBottleSlash),
    flagGlutenFree: fontAwesomeBrandIcon(faWheatSlash),
    flagRegional: fontAwesomeBrandIcon(faTractor),
    flagVegetarian: fontAwesomeBrandIcon(vLabel),
    flagVegan: fontAwesomeBrandIcon(vLabel),
} satisfies Partial<Record<keyof BrandIcons, (props: BrandIconProps) => ReactNode>>);

function fontAwesomeBrandIcon(icon: IconDefinition) {
    return ({ size, style, ...props }: BrandIconProps) => (
        <FontAwesomeIcon fixedWidth icon={icon} fontSize={size} style={{ ...style }} {...props} />
    );
}

function SosIcon({ size, active }: { size: number | string; active?: boolean }) {
    return (
        <div
            css={[
                {
                    position: 'relative',
                    fontSize: size,
                    width: '1em',
                    height: '1em',
                    fontWeight: 'bold',
                    backgroundColor: vars.cartBackgroundContrastText,
                    color: vars.cartBackgroundMain,
                    borderRadius: '50%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1,
                },
                active && {
                    backgroundColor: 'var(--warningColor)',
                    color: 'var(--lightColor)',

                    '&:before': {
                        content: '""',
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'var(--warningColor)',
                        borderRadius: '50%',
                        zIndex: -1,
                        '@keyframes pulse': {
                            '100%': {
                                transform: 'scale(2)',
                                opacity: 0,
                            },
                        },
                        animationName: 'pulse',
                        animationDuration: '1s',
                        animationIterationCount: 'infinite',
                    },
                },
            ]}
        >
            ?
        </div>
    );
}
